
import DialogMixins from '@/mixins/DialogMixins';
import WidgetMixins from '@/mixins/WidgetMixins';
import { loadWidget } from '@/utils/helpers';
import Component, { mixins } from 'vue-class-component';
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import { required, max, regex } from 'vee-validate/dist/rules';
import { Watch } from 'vue-property-decorator';
import { Getter, namespace } from 'vuex-class';
import { debounce } from 'lodash';
import {
  IContact,
  ICreditBalance,
  IGroup,
  IMessage,
  ISenderID,
  ISubAccount,
  ISubAccountDetails,
  IUser,
} from '@/types/types';

const groupModule = namespace('group');
const contactModule = namespace('contact');
const usersModule = namespace('users');
const smsModule = namespace('sms');

extend('required', {
  ...required,
  message: `Field is required`,
});
extend('max', {
  ...max,
  message: `Field cannot have more than 11 characters`,
});
extend('max_content', (value) => {
  if (value.length > 750) {
    return 'Message cannot have more than 750 characters';
  }
  return true;
});
extend('regex', {
  ...regex,
  message: `Field must only contain numbers seperated by commas or new line`,
});

const CONTACTS_SPLITTER_REGEX = /[\n,]+/gim;
const TIMEOUT = 2500;
@Component({
  name: 'QuickSMS',
  components: {
    InternetConnection: loadWidget('pages/InternetConnection'),
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
    ProgressBar: loadWidget('widgets/CircularProgressLoader'),
    BulkUploadDialog: loadWidget('widgets/BulkUpload'),
    AddSMSSenderID: loadWidget(`widgets/AddSenderID`),
    ValidationProvider,
    ValidationObserver,
  },
})
export default class QuickSMS extends mixins(WidgetMixins, DialogMixins) {
  @Getter('getCreditBalance') creditBalance!: number | ICreditBalance;
  @Getter('getUseIntlRoute') useIntlRoute!: boolean;
  @groupModule.Getter('getAllGroups') groups!: IGroup[];
  @Getter('getResetFormState') resetFormState!: boolean;
  @contactModule.Getter('getAllContacts') contacts!: IContact[];
  @smsModule.Getter('getSenderIDList') smsIDList!: Array<
    ISenderID & { uuid: string; id: number; slug: string }
  >;
  @usersModule.Getter('getSearchResults') subAccounts!: IUser[];

  e1 = 1;
  isSubAccountSelected = false;
  smsType: string[] = ['Quick', 'Flash'];
  isContactsLoading = false;
  hint = '';
  removeDuplicate = false;
  totalCredit = 0;
  subAccount: ISubAccount = {
    payload: null,
    id: null,
  };
  distinctContacts: string[] = [];

  @Watch('subAccount.payload', { deep: true })
  onSubAccountPayloadChange(payload: ISubAccountDetails): void {
    if (!payload) {
      this.isSubAccountSelected = false;
      this.subAccount.id = null;
      this.$store.dispatch('checkCreditBalance', null, { root: true });
      return;
    }
    this.isSubAccountSelected = true;
    this.subAccount.id = payload.id;
    this.$store.dispatch('updateCreditBalance', payload, {
      root: true,
    });
  }

  @Watch('contacts')
  onContactChange(payload: IContact[]): void {
    this.isContactsLoading = true;
    setTimeout(() => {
      this.sms.to = payload.map((contact) => contact.phone).join(',');
      this.isContactsLoading = false;
    }, TIMEOUT);
  }

  sms: IMessage = {
    to: '',
    from: '',
    type: 'Quick',
    message: '',
  };

  get recipientCount(): number {
    return this.sms.to
      ? this.sms.to.split(CONTACTS_SPLITTER_REGEX).filter(Boolean).length
      : 0;
  }

  bulkUpload(file: File): void {
    this.$store.dispatch('contact/processContactsFile', file);
  }

  @Watch('resetFormState')
  onFormChange(payload: boolean): void {
    if (payload) {
      this.sms = {
        to: '',
        message: '',
        from: this.sms.from,
        type: this.sms.type,
      };
    } else this.$store.dispatch('resetFormValues', false, { root: true });
  }

  @Watch('sms.to')
  onDestinationContentChange(phoneNumbers: string): void {
    this.totalCredit =
      Math.ceil(this.sms.message.length / 160) *
      (phoneNumbers?.split(CONTACTS_SPLITTER_REGEX).length ?? 1);
    this.hint = `${this.sms.message?.length ?? 0} characters · ${
      Math.ceil((this.sms.message?.length ?? 0) / 160) || 0
    } segment(s) of 160 chars · ${this.totalCredit} credits total`;
  }

  @Watch('sms.message')
  onMessageContentChange(content: string): void {
    this.totalCredit =
      Math.ceil(content.length / 160) *
      (this.sms.to?.split(CONTACTS_SPLITTER_REGEX).length ?? 1);
    this.hint = `${content.length} characters · ${
      Math.ceil(content.length / 160) || 0
    } segment(s) of 160 chars · ${this.totalCredit} credits total`;
  }

  handleOnSenderIDListRefresh = debounce(() => {
    this.$store.dispatch('sms/listSenderID');
  }, 500);

  refreshSenderIDList(): void {
    this.handleOnSenderIDListRefresh();
  }

  sendSMS(): void {
    if (this.e1 <= 2) {
      this.e1++;
      return;
    }
    this.distinctContacts = this.sms.to.split(CONTACTS_SPLITTER_REGEX);
    if (this.removeDuplicate) {
      this.distinctContacts = [...new Set(this.distinctContacts)];
    }
    const payload = {
      body: {
        messages: this.distinctContacts?.map((phone) => {
          return {
            to: phone,
            message: this.sms.message,
            from: this.sms.from,
            type: this.sms.type,
          };
        }),
        removeDuplicate: this.removeDuplicate,
      },
      ...(this.subAccount.id && {
        subAccountId: {
          'x-subaccount-id': this.subAccount.id,
        },
      }),
    };
    this.$store.dispatch('sms/sendBulkSMS', payload);
    setTimeout(() => {
      this.subAccount = {
        payload: null,
        id: null,
      };
      this.e1 = 1;
      this.removeDuplicate = false;
      this.$store.commit('contact/UPDATE_LIST', []);
    }, TIMEOUT);
  }

  created(): void {
    this.$store.dispatch('group/rawList');
    this.$store.dispatch('users/rawList');
    this.$store.dispatch('checkCreditBalance');
    this.$store.dispatch('sms/listSenderID');
  }
}
